import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, getSafeSession } from '@/lib/supabaseClient';
import type { Student } from '@/lib/types';
import { GRADE_LEVELS, computeGradeFromBirth } from '@/lib/constants';
import StudentFormModal from './components/StudentFormModal';
import StudentGradeGroup from './components/StudentGradeGroup';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import { useProfile } from '@/hooks/useProfile';

const UNGRADED_KEY = '__none__';

export default function Students() {
  const { profile, loading: profileLoading } = useProfile();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'no-birth'>('all');
  const [expandedGrades, setExpandedGrades] = useState<string[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Student | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Student | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // 프로필이 로드된 후에만 학생 목록을 불러와
    if (profileLoading || !profile) return;

    let mounted = true;

    async function fetchStudents() {
      setLoading(true);
      setError('');
      setDebugInfo('Step 1: fetchStudents 시작');

      try {
        // Step 1: 세션 확인
        setDebugInfo('Step 2: 세션 확인 중...');
        const session = await getSafeSession();
        if (!session) {
          setDebugInfo('Step 2: 세션 없음 → 로그인으로 이동');
          navigate('/login', { replace: true });
          return;
        }
        setDebugInfo(`Step 2: 세션 OK (uid: ${session.user.id.slice(0, 8)}...)`);

        // Step 2: 서버가 누구로 인식하는지 확인
        setDebugInfo('Step 3: 서버 인식 상태 조회 중...');
        const { data: debugData, error: debugErr } = await supabase.rpc('debug_auth_context');
        if (debugErr) {
          setDebugInfo(`Step 3 오류: ${debugErr.message}`);
        } else {
          setDebugInfo(`Step 3 OK: ${JSON.stringify(debugData, null, 2)}`);
        }

        // Step 3: 학원 ID 확인
        const academyId = profile.academy_id;
        if (!academyId) {
          setError('학원 배정 정보가 없어요. 관리자에게 문의해 주세요.');
          setDebugInfo('Step 4: 학원 ID 없음');
          if (mounted) setLoading(false);
          return;
        }
        setDebugInfo(`Step 4: 학원 ID = ${academyId}`);

        // Step 4: 학생 불러오기
        setDebugInfo('Step 5: 학생 데이터 조회 중...');
        const { data, error: err } = await supabase.rpc('get_students_by_academy', {
          p_academy_id: academyId,
        });

        if (err) {
          setDebugInfo(`Step 5 오류: ${err.message}`);
          throw err;
        }

        const rows = (data as Student[]) || [];

        // 생년월일 기준으로 학년 자동 보정
        // (해가 바뀌면 초5→초6, 초6→중1, 중3→고1, 고1→고2 처럼 자동으로 한 학년 올라가요)
        const corrected = await Promise.all(
          rows.map(async (s) => {
            const auto = computeGradeFromBirth(s.birth_date);
            if (auto && auto !== s.grade) {
              const { error: syncErr } = await supabase
                .from('students')
                .update({ grade: auto })
                .eq('id', s.id);
              if (!syncErr) return { ...s, grade: auto };
            }
            return s;
          })
        );

        setDebugInfo(`Step 5 OK: ${rows.length}명 조회 완료`);
        if (mounted) setStudents(corrected);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : '알 수 없는 오류';
        setDebugInfo(`예외 발생: ${msg}`);
        setError(`학생 목록을 불러오지 못했어요. (${msg})`);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchStudents();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLoading, profile]);

  function openAdd() {
    setEditing(null);
    setFormOpen(true);
  }

  async function reLogin() {
    // 세션을 완전히 비우고 로그인 화면으로 보내서 새로 로그인하게 해
    await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    navigate('/login', { replace: true });
  }

  function openEdit(student: Student) {
    setEditing(student);
    setFormOpen(true);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { error: err } = await supabase.from('students').delete().eq('id', deleteTarget.id);
      if (err) throw err;
      setDeleteTarget(null);
      // 삭제 후 목록 새로고침: 현재 프로필 기준으로 다시 불러오기
      window.location.reload();
    } catch (err) {
      setError('삭제에 실패했어요.');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  }

  const noBirthCount = students.filter((s) => !s.birth_date).length;

  const filtered = students.filter((s) => {
    if (filterMode === 'no-birth' && s.birth_date) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      s.name.toLowerCase().includes(q) ||
      (s.parent_name || '').toLowerCase().includes(q) ||
      (s.school || '').toLowerCase().includes(q) ||
      (s.grade || '').toLowerCase().includes(q)
    );
  });

  // 학년별로 묶고, 정해진 순서(중1~고3) 뒤에 기타 학년과 '학년 미지정'을 붙여요.
  const groups = useMemo(() => {
    const map: Record<string, Student[]> = {};
    filtered.forEach((s) => {
      const key = s.grade && s.grade.trim() ? s.grade : UNGRADED_KEY;
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });

    const ordered: string[] = [];
    GRADE_LEVELS.forEach((g) => {
      if (map[g]?.length) ordered.push(g);
    });
    Object.keys(map).forEach((g) => {
      if (g !== UNGRADED_KEY && !GRADE_LEVELS.includes(g)) ordered.push(g);
    });
    if (map[UNGRADED_KEY]?.length) ordered.push(UNGRADED_KEY);

    return ordered.map((key) => ({
      key,
      label: key === UNGRADED_KEY ? '학년 미지정' : key,
      students: map[key],
    }));
  }, [filtered]);

  const isSearching = search.trim().length > 0;
  // 검색 중에는 결과가 바로 보이도록 전부 펼쳐요.
  const isExpanded = (key: string) => isSearching || expandedGrades.includes(key);
  const allExpanded = groups.length > 0 && groups.every((g) => expandedGrades.includes(g.key));

  function toggleGrade(key: string) {
    setExpandedGrades((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }

  function toggleAll() {
    if (allExpanded) {
      setExpandedGrades([]);
    } else {
      setExpandedGrades(groups.map((g) => g.key));
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      {/* 현재 로그인 상태 표시 (디버깅 + 사용자 안심) */}
      <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-xs text-foreground-600">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span>
            <strong className="text-foreground-800">로그인 계정:</strong>{' '}
            {profile?.full_name || '알 수 없음'} ({profile?.role || '-'}){' '}
            {profile?.academy_name ? `· ${profile.academy_name}` : '· 학원 미배정'}
          </span>
          <span>
            <strong className="text-foreground-800">조회 결과:</strong>{' '}
            {loading ? '불러오는 중...' : `${students.length}명`}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground-950">학생 관리</h2>
          <p className="mt-1 text-sm text-foreground-500">
            총 {students.length}명의 학생이 등록되어 있어요.
          </p>
        </div>
        <button
          onClick={openAdd}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-add-line text-base"></i>
          학생 추가
        </button>
      </div>

      <div className="relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="이름, 학교, 학년, 보호자로 검색"
          className="w-full rounded-md border border-background-300 bg-white py-2.5 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
        />
      </div>

      {/* 필터 칩: 전체 / 생년월일 없음 */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setFilterMode('all')}
          className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
            filterMode === 'all'
              ? 'border-primary-500 bg-primary-500 text-background-50'
              : 'border-background-300 bg-white text-foreground-600 hover:bg-background-100'
          }`}
        >
          <i className="ri-list-check text-base"></i>
          전체 학생
          <span className="ml-0.5 opacity-80">{students.length}</span>
        </button>
        <button
          onClick={() => setFilterMode('no-birth')}
          className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
            filterMode === 'no-birth'
              ? 'border-accent-500 bg-accent-500 text-background-50'
              : 'border-background-300 bg-white text-foreground-600 hover:bg-background-100'
          }`}
        >
          <i className="ri-error-warning-line text-base"></i>
          생년월일 없음
          <span
            className={`ml-0.5 rounded-full px-1.5 text-xs font-bold ${
              filterMode === 'no-birth'
                ? 'bg-background-50/25 text-background-50'
                : noBirthCount > 0
                  ? 'bg-accent-100 text-accent-800'
                  : 'bg-background-100 text-foreground-500'
            }`}
          >
            {noBirthCount}
          </span>
        </button>
      </div>

      {filterMode === 'no-birth' && (
        <div className="flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
            <i className="ri-calendar-check-line text-lg"></i>
          </div>
          <div className="text-sm">
            <p className="font-semibold text-accent-900">
              생년월일이 없는 학생은 학년이 자동으로 바뀌지 않아요.
            </p>
            <p className="mt-0.5 text-accent-800/90">
              생년월일을 입력하면 해가 바뀔 때 학년이 자동으로 올라가요 (초5→초6, 초6→중1, 중3→고1, 고1→고2).
              아래 각 학생의 <strong>수정</strong> 버튼을 눌러 생년월일을 채워주세요.
              {noBirthCount === 0 && ' 지금은 생년월일이 비어 있는 학생이 없어요. 👍'}
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center justify-between rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-800">
          <span>{error}</span>
          <button
            onClick={() => {
              setLoading(true);
              setError('');
            }}
            className="cursor-pointer whitespace-nowrap font-semibold underline"
          >
            다시 시도
          </button>
        </div>
      )}

      {/* 서버 인식 상태 디버깅 */}
      {debugInfo && (
        <div className="rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3 text-xs text-secondary-800">
          <p className="font-semibold">서버 인식 상태 (디버깅):</p>
          <pre className="mt-1 whitespace-pre-wrap break-all font-mono text-secondary-700">{debugInfo}</pre>
        </div>
      )}

      {loading ? (
        <div className="flex h-48 items-center justify-center rounded-lg border border-background-200 bg-white">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-500"></i>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-white py-16 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-user-star-line text-2xl"></i>
          </div>
          <p className="mt-4 text-sm font-medium text-foreground-700">
            {students.length === 0
              ? '학생 목록을 불러오지 못했어요.'
              : filterMode === 'no-birth' && noBirthCount === 0
                ? '생년월일이 비어 있는 학생이 없어요.'
                : '검색 결과가 없어요.'}
          </p>
          <p className="mt-1 max-w-md text-xs text-foreground-400">
            {students.length === 0
              ? '로그인 세션이 만료됐거나 권한이 바뀌었을 수 있어요. 아래 버튼으로 다시 로그인하면 학생이 보일 거예요.'
              : filterMode === 'no-birth' && noBirthCount === 0
                ? '모든 학생의 생년월일이 입력되어 있어서 학년 자동계산이 정상적으로 동작해요.'
                : '다른 검색어로 시도해 보세요.'}
          </p>
          {students.length === 0 && (
            <button
              onClick={reLogin}
              className="mt-5 inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-login-box-line text-base"></i>
              다시 로그인하기
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {/* 학년별 요약 + 전체 펼치기/접기 */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {groups.map((g) => (
                <span
                  key={g.key}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600"
                >
                  {g.label}
                  <span className="font-bold text-foreground-800">{g.students.length}</span>
                </span>
              ))}
            </div>
            <button
              onClick={toggleAll}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-white px-3 py-1.5 text-xs font-medium text-foreground-600 transition-colors hover:bg-background-100"
            >
              <i className={allExpanded ? 'ri-collapse-diagonal-line' : 'ri-expand-diagonal-line'}></i>
              {allExpanded ? '전체 접기' : '전체 펼치기'}
            </button>
          </div>

          <div className="space-y-3">
            {groups.map((g) => (
              <StudentGradeGroup
                key={g.key}
                gradeKey={g.key}
                label={g.label}
                students={g.students}
                expanded={isExpanded(g.key)}
                onToggle={() => toggleGrade(g.key)}
                onEdit={openEdit}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>
        </div>
      )}

      <StudentFormModal
        open={formOpen}
        student={editing}
        academyId={profile?.academy_id || null}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          // 저장 후 목록 새로고침
          window.location.reload();
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="학생 삭제"
        message={`'${deleteTarget?.name}' 학생을 삭제할까요? 이 작업은 되돌릴 수 없어요.`}
        loading={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}